import { Component, computed, effect, inject, input } from '@angular/core';
import { FormControl } from '@angular/forms';

import { AvatarComponent } from '../../components/avatar/avatar.component';
import { TemplateDirective } from '../../directives/template.directive';
import { WorkPackagesService } from '../../services/work-package.service';
import { cast } from '../../utils';
import { WorkPackagePickerHandler } from '../smart-input-entity/work-package-picker.handler';
import {
  BaseWorkPackageDto,
  LaborCostLogWorkPackageDto,
  UnitCostLogWorkPackageDto,
} from '@api/backend-connector/models';
import { SmartInputEntityComponent } from 'src/app/components-form/smart-input-entity/smart-input-entity.component';
import { CallPipe } from 'src/app/pipes/call.pipe';

export type WorkPackagePickable =
  | Pick<BaseWorkPackageDto, 'id' | 'name' | 'budgetId' | 'budget'>
  | UnitCostLogWorkPackageDto
  | LaborCostLogWorkPackageDto;

const getColor = (pickable: WorkPackagePickable) => {
  return (pickable as unknown as Partial<BaseWorkPackageDto>).workPackageType?.color ?? undefined;
};

@Component({
  selector: 'app-work-package-picker',
  templateUrl: './work-package-picker.component.html',
  styleUrl: './work-package-picker.component.scss',
  standalone: true,
  imports: [CallPipe, SmartInputEntityComponent, TemplateDirective, AvatarComponent],
})
export class WorkPackagePickerComponent {
  private readonly workPackagesService = inject(WorkPackagesService);

  readonly projectId = input.required<string | undefined>();
  readonly control = input.required<FormControl<WorkPackagePickable | null | undefined>>();
  readonly placeholder = input('');
  readonly resetValue = input<WorkPackagePickable | undefined>(undefined);
  readonly contextCanChange = input<boolean>(false);

  readonly workPackagePickerHandler = computed(() => {
    void this.resetValue();
    return new WorkPackagePickerHandler(this.workPackagesService, { projectId: this.projectId() });
  });

  constructor() {
    effect(
      () => {
        if (this.contextCanChange()) {
          // when the core configuration changes (projectId), patch the control value
          // - in update forms it is patched by the manager (no-op)
          // - in create forms there is not an automation
          // Will patch if resetValue is not matching the control value.
          void this.projectId();
          if (this.control().getRawValue()?.id !== this.resetValue()?.id) {
            this.control().patchValue(this.resetValue());
          }
        }
      },
      { allowSignalWrites: true },
    );
  }
  readonly cast = cast<WorkPackagePickable>;
  readonly getColor = getColor;
}
